import PropTypes from 'prop-types';
import { use, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getMediaUrl } from '../../lib/flotiq-client/api-helpers';
import {
  getDataSize,
  getTestProps,
  isImagePreviewSupported,
} from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';

import UserContext from '../../contexts/UserContext';

import {
  AddVariantIcon,
  ArrowUpRightIcon,
  PencilIcon,
  TagPlusIcon,
  TrashIcon,
} from '../../images/shapes';

import Button from '../Button/Button';
import Card from '../Card/Card';
import Checkbox from '../Checkbox/Checkbox';
import DateContainer from '../DateContainer/DateContainer';
import LinkButton from '../LinkButton/LinkButton';
import StatusPill from '../StatusPill/StatusPill';
import Tooltip from '../Tooltip/Tooltip';

const disabledClasses =
  'disabled:text-white disabled:bg-transparent disabled:hover:bg-transparent ' +
  'disabled:active:!text-white dark:disabled:text-white dark:disabled:bg-transparent ' +
  'dark:disabled:hover:bg-transparent dark:disabled:active:!text-white';

const mediaCardDefaultPropMediaData = {
  fileName: 'Unknown',
  size: 'Unknown',
  imageUrl: '#',
  source: 'Unknown',
  sourceUrl: '#',
  width: '#',
  height: '#',
  type: 'image',
  internal: {
    createdAt: null,
  },
};

const MediaCard = ({
  mediaData = mediaCardDefaultPropMediaData,
  hideOverlay = false,
  thumbnail = '',
  onClick,
  isSelected = false,
  onDelete,
  onEdit,
  onVariantCreate,
  onTagEdit,
  onTagClick,
  additionalContainerClasses = '',
  additionalImageClasses = '',
  additionalThumbnailClasses,
  testId = '',
  activeSortOrder,
}) => {
  const { t } = useTranslation();
  const { permissions } = use(UserContext);
  const showImage = isImagePreviewSupported(mediaData?.extension);

  const canDelete = permissions.canCo(
    '_media',
    RolePermissions.PERMISSIONS_TYPES.DELETE,
  );
  const canCreateVariants = permissions.canCo(
    '_media',
    RolePermissions.PERMISSIONS_TYPES.UPDATE,
    'variants',
  );

  const imageUrl = useMemo(() => {
    if (mediaData.type === 'image' && showImage) {
      return getMediaUrl(mediaData, 300);
    }
  }, [mediaData, showImage]);

  const imageFileContainer = useMemo(() => {
    if (thumbnail) return thumbnail;
    if (
      mediaData.type !== 'image' ||
      (mediaData.type === 'image' && !showImage)
    ) {
      return (
        <div className="flex items-center justify-center h-full w-full">
          <div className="flex items-center justify-center rounded-full h-12 w-12 bg-blue-300">
            {mediaData.extension || ''}
          </div>
        </div>
      );
    }
    return '';
  }, [thumbnail, mediaData, showImage]);

  const imageUploadedDate = useMemo(() => {
    if (mediaData.internal?.createdAt) {
      return (
        <DateContainer
          additionalClasses="text-white text-sm sm:text-base"
          additionalTextClasses="font-semibold mr-2 text-sm sm:text-base"
          date={mediaData.internal?.createdAt}
          text={t('Media.MediaCard.ImageData.ImageUploadedDate')}
          isBold={false}
        />
      );
    }
    return null;
  }, [mediaData, t]);

  const addVariantTooltip = useMemo(() => {
    if (!canCreateVariants) return t('UserRoles.CannotPerformAction');
    if (!showImage) return t('MediaEdit.AddVariantDisabled');
    return t('MediaEdit.AddVariant');
  }, [canCreateVariants, showImage, t]);

  const imageOverlay = useMemo(() => {
    if (hideOverlay) return null;
    return (
      <div
        className="flex flex-col h-full p-2 sm:p-3 justify-between"
        {...getTestProps(testId, 'media-overlay')}
      >
        <Checkbox
          additionalContainerClasses="group-hover:block"
          additionalCheckboxClasses={twMerge(
            'border-0 group-hover:block group-hover:border dark:block',
            'checked:disabled:bg-blue checked:disabled:border-blue disabled:cursor-pointer dark:border-gray-200',
          )}
          checked={isSelected}
          circular
          {...getTestProps(testId, '', 'testId')}
        />
        <div
          className="hidden group-hover:flex flex-col items-start justify-between transition
          duration-200 ease-in-out grow"
        >
          <div className="text-white flex flex-col items-start justify-start mt-2 sm:mt-3">
            {mediaData.type === 'image' && (
              <div
                className="flex font-semibold text-sm sm:text-base"
                {...getTestProps(testId, 'image-dimensions')}
              >
                <Trans i18nKey="Media.MediaCard.ImageData.ImageDimensions">
                  Dimensions:
                  <span className="whitespace-nowrap ml-1 font-normal">
                    {{ width: mediaData.width }} x{' '}
                    {{ height: mediaData.height }} px
                  </span>
                </Trans>
              </div>
            )}
            <div
              className="flex font-semibold text-sm sm:text-base"
              {...getTestProps(testId, 'image-size')}
            >
              <Trans i18nKey="Media.MediaCard.ImageData.ImageByteSize">
                Size:
                <span className="whitespace-nowrap ml-1 font-normal">
                  {{ size: getDataSize(mediaData.size) }}
                </span>
              </Trans>
            </div>
            <div className="flex text-white">{imageUploadedDate}</div>
            {mediaData.type === 'image' && (
              <div
                className="flex font-semibold text-sm sm:text-base"
                {...getTestProps(testId, 'variants')}
              >
                <Trans i18nKey="Media.MediaCard.ImageData.ImageVariants">
                  Variants:
                  <span className="whitespace-nowrap ml-1 font-normal">
                    {{ variants: mediaData.variants?.length || 0 }}
                  </span>
                </Trans>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 w-full">
            {onEdit && (
              <Tooltip
                tooltip={t('MediaEdit.Edit')}
                tooltipPlacement="topCenter"
                phoneTooltipPlacement="topCenter"
              >
                <Button
                  buttonColor="blueBordered"
                  buttonSize="xs"
                  iconColor="white"
                  iconImage={
                    <PencilIcon className="w-4 group-hover:text-white" />
                  }
                  additionalClasses="shrink-0 bg-transparent"
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit(mediaData);
                  }}
                  {...getTestProps(testId, 'edit', 'testId')}
                />
              </Tooltip>
            )}
            {onVariantCreate && mediaData.type === 'image' && (
              <Tooltip
                tooltip={addVariantTooltip}
                tooltipPlacement="topCenter"
                phoneTooltipPlacement="topCenter"
              >
                <Button
                  buttonSize="xs"
                  iconImage={<AddVariantIcon className="w-4 " />}
                  additionalClasses={
                    'shrink-0 bg-transparent border-lime dark:border-lime hover:bg-lime hover:border-lime ' +
                    disabledClasses
                  }
                  disabled={!showImage || !canCreateVariants}
                  onClick={(event) => {
                    event.stopPropagation();
                    onVariantCreate(mediaData);
                  }}
                  {...getTestProps(testId, 'create-variant', 'testId')}
                />
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip
                tooltip={
                  canDelete
                    ? t('Media.Delete')
                    : t('UserRoles.CannotPerformAction')
                }
                tooltipPlacement="topCenter"
                phoneTooltipPlacement="topCenter"
              >
                <Button
                  buttonColor="redBordered"
                  buttonSize="xs"
                  iconColor="red"
                  iconImage={
                    <TrashIcon className="w-4 group-hover:text-white" />
                  }
                  additionalClasses={
                    'shrink-0 bg-transparent ' + disabledClasses
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete(mediaData);
                  }}
                  disabled={!canDelete}
                  {...getTestProps(testId, 'delete', 'testId')}
                />
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    );
  }, [
    hideOverlay,
    testId,
    isSelected,
    mediaData,
    imageUploadedDate,
    onEdit,
    t,
    onVariantCreate,
    addVariantTooltip,
    showImage,
    canCreateVariants,
    onDelete,
    canDelete,
  ]);

  const tagsList = useMemo(() => {
    const tags = mediaData.tags;
    return (
      <>
        {onTagEdit && (
          <Button
            title={t('Media.MediaManagement.AddTags')}
            buttonColor="borderless"
            iconImage={
              <TagPlusIcon
                className={twMerge(
                  'min-w-[1rem] w-4 h-4 cursor-pointer',
                  'text-slate-400 dark:text-slate-400 hover:text-blue dark:hover:text-blue',
                )}
              />
            }
            additionalClasses="w-fit !p-1 mr-2"
            onClick={() => onTagEdit(mediaData)}
            {...getTestProps(testId, 'edit-tags', 'testId')}
          />
        )}
        {tags?.length > 0 && (
          <div
            className="flex flex-wrap"
            {...getTestProps(testId, 'tags-list')}
          >
            {tags
              .filter((tag) => tag)
              .map((tag) => {
                return (
                  <StatusPill
                    onClick={() => onTagClick?.(tag.id, tag.name)}
                    key={tag.id}
                    status={tag.name}
                    containerClasses="mx-1 !p-0 !h-5 my-0.5 !px-1.5 !bg-gray-200 dark:!bg-gray-900 cursor-pointer
                    rounded leading-none flex items-center justify-center dark:text-slate-400 dark:border-slate-400"
                  />
                );
              })}
          </div>
        )}
      </>
    );
  }, [mediaData, onTagClick, onTagEdit, t, testId]);

  const activeSortValueLabel = useMemo(() => {
    if (activeSortOrder === 'internal.createdAt') {
      return (
        <DateContainer
          additionalClasses={'font-medium'}
          additionalTextClasses={'mr-2 font-normal'}
          date={mediaData.internal?.createdAt}
          text={t('Media.MediaCard.ImageData.ImageUploadedDate')}
          isBold={false}
        />
      );
    }

    if (activeSortOrder === 'extension') {
      return (
        <Trans i18nKey="Media.MediaCard.ImageData.ImageExtension">
          Extension:
          <span className="whitespace-nowrap ml-1 font-normal">
            {{ extension: mediaData.extension }}
          </span>
        </Trans>
      );
    }

    return (
      <Trans i18nKey="Media.MediaCard.ImageData.ImageByteSize">
        Size:
        <span className="whitespace-nowrap ml-1 font-normal">
          {{ size: getDataSize(mediaData.size) }}
        </span>
      </Trans>
    );
  }, [
    activeSortOrder,
    mediaData.extension,
    mediaData.internal?.createdAt,
    mediaData.size,
    t,
  ]);

  return (
    <Card
      testId={testId}
      darkenOnHover
      additionalContainerClasses={twMerge(
        'w-auto border border-slate-200 dark:border-0 overflow-visible rounded-sm',
        additionalContainerClasses,
      )}
      additionalImageClasses={twMerge(
        'object-scale-down h-40 w-auto sm:h-52 dark:bg-gray-700',
        additionalImageClasses,
      )}
      additionalBodyClasses="h-full"
      additionalImageContainerClasses={twMerge(
        'overflow-visible',
        mediaData.type !== 'media'
          ? 'h-40 w-auto sm:h-52 dark:bg-gray-700'
          : '',
      )}
      body={
        <div
          className="w-full h-full px-2 pb-d4 sm:px-4 sm:py-2 text-xs sm:text-sm bg-white dark:bg-slate-950 font-light
          relative"
        >
          <LinkButton
            link={getMediaUrl(mediaData)}
            target="_blank"
            rel="noreferrer"
            iconImage={<ArrowUpRightIcon className="w-3" />}
            buttonColor="borderless"
            buttonSize="xs"
            iconColor="blue"
            iconPosition="end"
            additionalClasses={`py-2 text-sm sm:text-base 2xl:text-base font-normal text-indigo-950 
              dark:text-gray-200 max-w-full focus:hover:underline`}
            additionalChildrenClasses="truncate"
            noPaddings
            {...getTestProps(testId, `${mediaData.id}-open-file`, 'testId')}
          >
            {mediaData.fileName}
          </LinkButton>
          <div className="flex items-center pt-2 pb-1 sm:pb-3 relative">
            <div className="absolute left-0 text-sm sm:text-base dark:text-gray-200">
              {activeSortValueLabel}
            </div>
          </div>
          <div
            className="mt-3.5 border-t border-gray-200 dark:border-slate-800 pt-2.5 flex items-center cursor-auto"
            onClick={(event) => event.stopPropagation()}
          >
            {tagsList}
          </div>
        </div>
      }
      additionalThumbnailClasses={additionalThumbnailClasses}
      imageOverlay={!hideOverlay ? imageOverlay : ''}
      imageUrl={imageUrl}
      onClick={() => onClick?.(mediaData)}
      thumbnail={imageFileContainer}
      imageUrlAlt={mediaData.fileName}
    />
  );
};

MediaCard.propTypes = {
  /**
   * Object with image data {url, size, width,height, author, source}
   */
  mediaData: PropTypes.object,
  /**
   * If overlay should be hidden
   */
  hideOverlay: PropTypes.bool,
  /**
   * On card click callback
   */
  onClick: PropTypes.func,
  /**
   * If card is selected
   */
  isSelected: PropTypes.bool,
  /**
   * On delete button click callback
   */
  onDelete: PropTypes.func,
  /**
   * On delete button click callback
   */
  onEdit: PropTypes.func,
  /**
   * On variant create button
   */
  onVariantCreate: PropTypes.func,
  /**
   * On delete button click callback
   */
  onTagEdit: PropTypes.func,
  /**
   * On delete button click callback
   */
  onTagClick: PropTypes.func,
  /**
   * Card file container override
   */
  thumbnail: PropTypes.node,
  /**
   * Additional card container classes
   */
  additionalContainerClasses: PropTypes.string,
  /**
   * Additional image classes
   */
  additionalImageClasses: PropTypes.string,
  /**
   * Additional thumbnail classes
   */
  additionalThumbnailClasses: PropTypes.string,
  /**
   * Card test id
   */
  testId: PropTypes.string,
  /**
   * Active sort order
   */
  activeSortOrder: PropTypes.any,
};

export default MediaCard;
